export class LinkedList<T> {
  private readonly data: Array<T>;
  private readonly length: number;
  private index = 0;

  public constructor(data: Array<T>) {
    this.data = data;
    this.length = data.length;
  }

  private get isEmpty(): boolean {
    return this.size <= 0;
  }

  private get isFirst(): boolean {
    return this.currentIndex === 0;
  }

  private get isLast(): boolean {
    return this.currentIndex === this.size - 1;
  }

  public get currentElement(): T {
    return this.getElement(this.index);
  }

  public get currentIndex(): number {
    return this.index;
  }

  public get size(): number {
    return this.length;
  }

  private isElementFilter(filter: keyof T, expected): boolean {
    return this.currentElement[filter] === expected;
  }

  public first(): this {
    if (!this.isEmpty) {
      this.index = 0;
    }
    return this;
  }

  public firstFilter(filter: keyof T, expected: boolean): this {
    this.first();
    if (!this.isElementFilter(filter, expected)) {
      this.nextFilter(filter, expected);
    }
    return this;
  }

  public forEach(
    callback: (element: T, index?: number, array?: T[]) => void
  ): void {
    this.data.forEach(callback);
  }

  public getElement(index: number): T {
    if (this.isEmpty || index < 0 || index > this.size - 1) {
      return null;
    }
    return this.data[index];
  }

  public last(): this {
    if (!this.isEmpty) {
      this.index = this.size - 1;
    }
    return this;
  }

  public lastFilter(filter: keyof T, expected: boolean): this {
    this.last();
    if (!this.isElementFilter(filter, expected)) {
      this.prevFilter(filter, expected);
    }
    return this;
  }

  public map(
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    callback: (element: T, index?: number, array?: T[]) => any
  ): LinkedList<T> {
    return new LinkedList<T>(this.data.map(callback));
  }

  public next(): this {
    if (!this.isLast) {
      this.index++;
    }

    return this;
  }

  public nextFilter(filter: keyof T, expected: boolean): LinkedList<T> {
    if (!this.isLast) {
      this.next();

      if (!this.isElementFilter(filter, expected)) {
        return this.nextFilter(filter, expected);
      }
    }

    return this;
  }

  public prev(): this {
    if (!this.isFirst) {
      this.index--;
    }

    return this;
  }

  public prevFilter(filter: keyof T, expected: boolean): LinkedList<T> {
    if (!this.isFirst) {
      this.prev();

      if (!this.isElementFilter(filter, expected)) {
        return this.prevFilter(filter, expected);
      }
    }

    return this;
  }

  public setIndex(index: number): void {
    this.index = index;
  }
}
